import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  colors,
  dialogActionsSx,
  primaryButtonSx,
  secondaryButtonSx,
  typo,
} from '../../../../theme/tokens';
import { WriteButton } from '../../../../components/ui';
import {
  GroupInput,
  GroupLevel,
  GroupScope,
  TenantGroup,
  TenantGroupMember,
} from '../../../../api/tenantGroupsApi';
import { useScopeEntities } from '../hooks/useScopeEntities';
import EntityLabel from './EntityLabel';

interface Props {
  open: boolean;
  /** null = create a new group; otherwise edit this group. */
  group: TenantGroup | null;
  onClose: () => void;
  onSubmit: (input: GroupInput) => void;
  saving: boolean;
  onNotify: (msg: string, sev: 'success' | 'error' | 'info') => void;
}

const SCOPE_LABEL: Record<GroupScope, string> = {
  customer: 'Customer',
  sponsor: 'Sponsor',
  tenant: 'Tenant',
};
// NOTE: no 'global'/'All tenants' — a group member is always a concrete
// customer / sponsor / tenant scope point (locked decision).
const SCOPES: GroupScope[] = ['customer', 'sponsor', 'tenant'];

const memberKey = (m: TenantGroupMember): string => `${m.scope_type}:${m.scope_code}`;

/**
 * Create/edit a tenant group (CPT-1133): name, access level (baked into the
 * group), and members picked from concrete tenant/customer/sponsor scope
 * points — no 'global' option (locked decision).
 */
const GroupEditorDrawer: React.FC<Props> = ({
  open,
  group,
  onClose,
  onSubmit,
  saving,
  onNotify,
}) => {
  const isEdit = !!group;
  const [name, setName] = useState('');
  const [level, setLevel] = useState<GroupLevel>('read');
  const [members, setMembers] = useState<TenantGroupMember[]>([]);
  const [scopeType, setScopeType] = useState<GroupScope>('tenant');
  const [inputValue, setInputValue] = useState('');

  const { optionsFor, nameFor, loading } = useScopeEntities(open);

  // Reset the form whenever the drawer opens or the target group changes.
  useEffect(() => {
    if (open) {
      setName(group?.name ?? '');
      setLevel(group?.level ?? 'read');
      setMembers(group ? group.members.map((m) => ({ ...m })) : []);
      setScopeType('tenant');
      setInputValue('');
    }
  }, [open, group]);

  const memberSet = useMemo(() => new Set(members.map(memberKey)), [members]);

  const options = useMemo(
    () => optionsFor(scopeType).filter((o) => !memberSet.has(`${scopeType}:${o.code}`)),
    [optionsFor, scopeType, memberSet],
  );

  const addMember = (code: string) => {
    const m: TenantGroupMember = { scope_type: scopeType, scope_code: code };
    if (memberSet.has(memberKey(m))) {
      onNotify('That member is already in this group.', 'info');
      return;
    }
    setMembers((prev) => [...prev, m]);
    setInputValue('');
  };

  const removeMember = (m: TenantGroupMember) =>
    setMembers((prev) => prev.filter((x) => memberKey(x) !== memberKey(m)));

  const canSave = name.trim().length > 0 && !saving;

  const handleSave = () => {
    if (!canSave) return;
    onSubmit({ name: name.trim(), level, members });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 0, display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${colors.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography component="h2" sx={{ ...typo.sectionTitle }}>
          {isEdit ? 'Edit group' : 'New tenant group'}
        </Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, overflowY: 'auto', flex: 1 }}>
        {/* Name */}
        <Box>
          <Typography sx={{ ...typo.fieldLabel }}>Group name</Typography>
          <TextField
            fullWidth
            size="small"
            value={name}
            placeholder="e.g. Northeast Sponsors"
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 100, 'aria-label': 'Group name' }}
            InputProps={{ sx: { borderRadius: '10px' } }}
          />
        </Box>

        {/* Level — baked into the group */}
        <Box>
          <Typography sx={{ ...typo.fieldLabel }}>Access level</Typography>
          <ToggleButtonGroup exclusive size="small" value={level} onChange={(_e, v) => { if (v) setLevel(v); }}>
            <ToggleButton value="read" sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 600 }}>Read</ToggleButton>
            <ToggleButton value="write" color="warning" sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 600 }}>Write</ToggleButton>
          </ToggleButtonGroup>
          <Typography sx={{ ...typo.tinyText, mt: 0.75 }}>
            The level is baked into the group — every member is granted {level} access. For the same
            entities at a different level, create a separate group.
          </Typography>
        </Box>

        <Divider />

        {/* Member picker */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography sx={{ ...typo.fieldLabel }}>Add members</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={scopeType}
            onChange={(_e, v) => { if (v) { setScopeType(v); setInputValue(''); } }}
            sx={{ flexWrap: 'wrap' }}
          >
            {SCOPES.map((s) => (
              <ToggleButton key={s} value={s} sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 600 }}>
                {SCOPE_LABEL[s]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Autocomplete
            size="small"
            options={options}
            value={null}
            inputValue={inputValue}
            onInputChange={(_e, v, reason) => { if (reason !== 'reset') setInputValue(v); }}
            loading={loading}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.code === b.code}
            onChange={(_e, v) => { if (v) addMember(v.code); }}
            renderOption={(props, o) => {
              const { key, ...liProps } = props as { key?: React.Key } & React.HTMLAttributes<HTMLLIElement>;
              return (
                <Box component="li" key={key} {...liProps} sx={{ display: 'block', py: 0.75 }}>
                  <EntityLabel scope={scopeType} name={o.name} code={o.code} />
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={`Search a ${scopeType}…`}
                InputProps={{ ...params.InputProps, sx: { borderRadius: '10px' } }}
              />
            )}
          />
        </Box>

        {/* Current members */}
        <Box>
          <Typography sx={{ ...typo.fieldLabel, mb: 1 }}>
            Members{members.length > 0 ? ` (${members.length})` : ''}
          </Typography>
          {members.length === 0 ? (
            <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: colors.textTertiary }}>
              No members yet — add tenants, sponsors, or customers above.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {members.map((m) => (
                <Box
                  key={memberKey(m)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
                    border: `1px solid ${colors.borderLight}`, borderRadius: '10px',
                  }}
                >
                  <Chip size="small" label={SCOPE_LABEL[m.scope_type]} sx={{ height: 20, fontSize: 10, fontWeight: 600 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <EntityLabel scope={m.scope_type} name={nameFor(m.scope_type, m.scope_code)} code={m.scope_code} />
                  </Box>
                  <Tooltip title="Remove member">
                    <IconButton size="small" onClick={() => removeMember(m)}>
                      <DeleteOutlineIcon sx={{ fontSize: 18, color: colors.errorIcon }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Footer actions */}
      <Box sx={{ ...dialogActionsSx, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={secondaryButtonSx}>Cancel</Button>
        <WriteButton ignoreEnvReadOnly onClick={handleSave} disabled={!canSave} variant="contained" sx={primaryButtonSx}>
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create group'}
        </WriteButton>
      </Box>
    </Drawer>
  );
};

export default GroupEditorDrawer;
